import { ChevronDown, Sparkles, MessageCircle, ArrowRight } from 'lucide-react';
import { useState, useRef, useMemo } from 'react';
import {
    motion,
    AnimatePresence,
    useInView,
    useScroll,
    useTransform,
    useSpring,
    useReducedMotion,
} from 'motion/react';

export function FAQ() {
    const [openIndex, setOpenIndex] = useState(0); // erstes offen wirkt “lebendiger”
    const ref = useRef(null);
    const reduceMotion = useReducedMotion();
    const isInView = useInView(ref, { once: true, margin: '-120px' });

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });

    const blobYRaw = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
    const blobY = useSpring(blobYRaw, { stiffness: 90, damping: 22 });

    const faqs = useMemo(
        () => [
            {
                question: 'Wie lange dauert die Entwicklung einer Website?',
                answer:
                    'Das hängt vom Umfang ab. Eine schlanke Unternehmensseite ist oft in 2–3 Wochen realistisch. Umfangreichere Projekte (viele Unterseiten, komplexe Inhalte, besondere Funktionen) dauern entsprechend länger. Nach dem Erstgespräch bekommen Sie eine klare Timeline.',
            },
            {
                question: 'Was kostet eine professionelle Website?',
                answer:
                    'Die Kosten richten sich nach Anforderungen, Umfang und gewünschten Features. Eine einfache Unternehmenswebsite startet typischerweise ab ca. 800€. Nach unserem Erstgespräch erhalten Sie ein transparentes Angebot mit klaren Leistungsbausteinen.',
            },
            {
                question: 'Kann ich meine Website selbst aktualisieren?',
                answer:
                    'Ja. Auf Wunsch integriere ich ein benutzerfreundliches CMS, damit Sie Inhalte, Bilder und Texte selbst pflegen können. Zusätzlich bekommen Sie eine kurze Einweisung, damit alles sicher sitzt.',
            },
            {
                question: 'Ist die Website für mobile Geräte optimiert?',
                answer:
                    'Absolut. Jede Website ist responsive (mobile-first) und passt sich automatisch an Smartphones, Tablets und Desktop an. Mobile Optimierung ist außerdem ein wichtiger Faktor für SEO und Conversion.',
            },
            {
                question: 'Bieten Sie auch Wartung und Support an?',
                answer:
                    'Ja. Es gibt Wartungspakete für Updates, Sicherheit, Backups und Support. So bleibt Ihre Website langfristig stabil, sicher und aktuell.',
            },
            {
                question: 'Wie sieht es mit SEO aus?',
                answer:
                    'Technisches SEO ist bei mir standardmäßig dabei: saubere Struktur, schnelle Ladezeiten, mobile Optimierung und Meta-Basics. Für tiefergehende Strategien (Content, Keyword-Cluster, Linkaufbau) biete ich separate Pakete an.',
            },
            {
                question: 'Bekomme ich die volle Kontrolle über meine Website?',
                answer:
                    'Ja. Nach Projektabschluss gehört alles Ihnen: Zugangsdaten, Dateien und Kontrolle. Ich unterstütze gern weiter, aber Sie sind nie abhängig.',
            },
            {
                question: 'Was passiert nach dem Launch?',
                answer:
                    'Nach dem Launch gibt es eine kurze Stabilitätsphase: Ich prüfe, ob alles sauber läuft. Sie erhalten eine Einweisung und ggf. Dokumentation. Für laufende Optimierung können wir auf Wunsch in Support-Pakete wechseln.',
            },
        ],
        []
    );

    return (
        <section
            id="faq"
            className="relative py-24 px-4 overflow-hidden"
            ref={ref}
        >
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-purple-50" />

            {/* Ambient blob */}
            <motion.div
                className="absolute -top-24 right-[-140px] w-[520px] h-[520px] rounded-full blur-3xl opacity-25"
                style={{
                    background: 'radial-gradient(circle, rgba(147,51,234,0.35), transparent 60%)',
                    y: blobY,
                }}
                animate={reduceMotion ? undefined : { scale: [1, 1.1, 1], opacity: [0.16, 0.28, 0.16] }}
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.22) 1px, transparent 1px)',
                    backgroundSize: '64px 64px',
                    maskImage: 'radial-gradient(circle at 50% 30%, black, transparent 70%)',
                }}
                aria-hidden="true"
            />

            <div className="relative z-10 max-w-4xl mx-auto">
                {/* Header */}
                <motion.div
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 26 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-border/60 bg-white/70 backdrop-blur px-4 py-2 shadow-sm">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-blue-600 text-white">
              <Sparkles className="h-4 w-4" />
            </span>
                        <span className="text-sm text-gray-700">Häufige Fragen</span>
                        <span className="ml-1 rounded-full bg-black/5 px-2 py-0.5 text-xs text-gray-700">
              Schnell beantwortet
            </span>
                    </div>

                    <h2 className="mt-6 text-5xl md:text-6xl tracking-tight">
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Antworten
            </span>{' '}
                        <span className="text-gray-900">auf die wichtigsten Fragen</span>
                    </h2>

                    <p className="mt-5 text-xl text-gray-600">
                        Klar, ehrlich und ohne Fachchinesisch.
                    </p>
                </motion.div>

                {/* FAQ list */}
                <div className="space-y-4">
                    {faqs.map((faq, index) => {
                        const isOpen = openIndex === index;

                        return (
                            <motion.div
                                key={faq.question}
                                className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-white/75 backdrop-blur shadow-lg"
                                initial={{ opacity: 0, y: 18 }}
                                animate={isInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                whileHover={{ y: -2 }}
                            >
                                {/* Glow */}
                                <div
                                    className={`pointer-events-none absolute -inset-1 blur-xl transition-opacity duration-300 ${
                                        isOpen ? 'opacity-100' : 'opacity-0'
                                    } bg-gradient-to-r from-purple-500/15 via-blue-500/15 to-pink-500/15`}
                                />

                                <button
                                    className="relative w-full px-6 py-5 text-left flex items-start justify-between gap-6"
                                    onClick={() => setOpenIndex(isOpen ? null : index)}
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-3">
                      <span
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-xl shadow-sm border ${
                              isOpen
                                  ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white border-transparent'
                                  : 'bg-white/80 text-gray-800 border-gray-200/70'
                          }`}
                      >
                        <MessageCircle className="h-4 w-4" />
                      </span>
                                            <span className="text-lg text-gray-900 leading-snug">
                        {faq.question}
                      </span>
                                        </div>
                                    </div>

                                    <motion.div
                                        className={`mt-1 flex-shrink-0 h-10 w-10 rounded-xl border border-gray-200/70 bg-white/80 flex items-center justify-center`}
                                        animate={{ rotate: isOpen ? 180 : 0 }}
                                        transition={{ duration: 0.25 }}
                                        aria-hidden="true"
                                    >
                                        <ChevronDown className="w-5 h-5 text-purple-700" />
                                    </motion.div>
                                </button>

                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.28, ease: 'easeOut' }}
                                            className="relative overflow-hidden"
                                        >
                                            <div className="px-6 pb-5 pt-0 text-gray-600 leading-relaxed">
                                                {faq.answer}
                                            </div>

                                            {/* bottom divider */}
                                            <div className="mx-6 mb-5 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Bottom CTA */}
                <motion.div
                    className="mt-10"
                    initial={{ opacity: 0, y: 16 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.5, duration: 0.6 }}
                >
                    <div className="rounded-2xl border border-gray-200/70 bg-white/75 backdrop-blur shadow-lg overflow-hidden">
                        <div className="p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                            <div>
                                <div className="text-sm text-gray-600">Noch Fragen offen?</div>
                                <div className="text-lg font-semibold text-gray-900">
                                    Schreiben Sie mir kurz – ich antworte meistens innerhalb von 24 Stunden.
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 px-5 py-3 text-white font-medium shadow-md hover:from-purple-700 hover:to-blue-700 transition-colors"
                            >
                                Kontakt aufnehmen
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
